// Bridges the Teamlio PF Filing extension (../../../server/teamlio extension)
// for TDS challan filing (Income Tax e-filing portal, e-Pay Tax) via
// window.postMessage. relay.js (a content script the extension injects on
// this origin) forwards these to the extension's background script and back.
// Same bridge/extension as pfExtension.ts — just different message types
// (it-extract.js drives eportal.incometax.gov.in instead of the EPFO portal).
// Shared with the TDS/PF twin of this file: the same check against the
// same extension, and it has to end while the click that started it still
// counts as user activation.
import { PING_ATTEMPTS, PING_TIMEOUT_MS } from './extensionPing';

const REQUEST_SOURCE = 'teamlio-pf-extension-page';
const RESPONSE_SOURCE = 'teamlio-pf-extension';

const IT_TIMEOUT_MS = 6000;

// Published Chrome Web Store listing for the Teamlio PF Filing extension —
// same install link pfExtension.ts uses, since it's the same extension.
export const PF_EXTENSION_WEBSTORE_URL =
  'https://chromewebstore.google.com/detail/teamlio-pf-filing/bnjnabfklgnpkgggiakfieoghbommopa';

let requestCounter = 0;

function sendToExtension(type: string, payload: Record<string, unknown>, timeoutMs: number): Promise<any> {
  return new Promise((resolve, reject) => {
    const requestId = `${Date.now()}-${requestCounter++}`;

    const handler = (event: MessageEvent) => {
      if (event.source !== window) return;
      if ((event.data as any)?.source !== RESPONSE_SOURCE) return;
      if ((event.data as any)?.type !== 'response') return;
      if ((event.data as any)?.requestId !== requestId) return;
      window.removeEventListener('message', handler);
      clearTimeout(timer);
      resolve((event.data as any).response);
    };
    window.addEventListener('message', handler);

    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('extension_timeout'));
    }, timeoutMs);

    window.postMessage({ source: REQUEST_SOURCE, requestId, type, ...payload }, window.location.origin);
  });
}

export async function pingItExtension(): Promise<boolean> {
  for (let attempt = 0; attempt < PING_ATTEMPTS; attempt += 1) {
    try {
      const response = await sendToExtension('ping', {}, PING_TIMEOUT_MS);
      if (response?.ok) return true;
    } catch {
      // timed out — retry once
    }
  }
  return false;
}

export interface ItFileParams {
  username: string;
  password: string;
  // The caller's own session access token — the extension's background
  // service worker carries it so the flow can write the payment receipt back
  // to hrms_prod itself (PATCH /tds/challan/{id}/receipt).
  authToken?: string;
  // The draft challan this run belongs to. Created in hrpayroll BEFORE the
  // portal is touched, so the CRN and then the BSR code / serial number read
  // off the portal have a row to attach to. Omitted, the extension just fills
  // the form and reports nothing back.
  challanId?: string;
  // hrpayroll's own API origin — validated against an allowlist in
  // background.js, never trusted blindly.
  apiBaseUrl?: string;
  // The challan's Financial Year, e.g. "2026-27" — picks the Tax Year on the
  // e-Pay Tax "New Payment" screen instead of whatever the portal defaults to.
  taxYear: string;
  // The TDS section chosen on the Payroll Reports page (e.g. "192") — the
  // extension matches it against the portal's Description dropdown so the
  // same section picked in hrpayroll gets picked on the portal.
  section: string;
  // { tdsAmount, interestAmount, lateFeeAmount, penaltyAmount, otherAmount }
  // fill the (a) Tax / (d)-(g) Other Payments fields once the section is
  // selected — same numbers as the Payroll Reports Statutory tab.
  // The portal's (a) Tax / (b) Surcharge / (c) Education Cess on the
  // section's own card, and (d) Interest / (e) Late Fee / (f) Penalty /
  // (g) Others on the card below it.
  amounts: {
    tdsAmount: number;
    surchargeAmount?: number;
    eduCessAmount?: number;
    interestAmount?: number;
    lateFeeAmount?: number;
    penaltyAmount?: number;
    otherAmount?: number;
  };
}

// Opens the Income Tax e-filing portal in a foreground tab (user solves the
// CAPTCHA), logs in, then walks e-File → e-Pay Tax → Act 2025 → New Payment →
// Tax Year → Pay TDS/TCS → Deductee Type → section → fills amounts. Stops
// there — the final Submit/Pay always stays a user action.
/** Sign in to the e-filing portal and stop. No challan, no statement -- the
 *  same shape the EPFO and ESIC sign-ins have, so "can we even get in?" is
 *  one button rather than a filing run that has to be abandoned. */
export async function startItLogin(params: { username: string; password: string;
  authToken?: string; apiBaseUrl?: string }) {
  const response = await sendToExtension(
    'itFileStart',
    { mode: 'login', username: params.username, password: params.password,
      authToken: params.authToken, apiBaseUrl: params.apiBaseUrl },
    IT_TIMEOUT_MS,
  );
  if (!response?.ok) throw new Error(response?.error || 'extension_refused');
  return response;
}

/** Read what the portal says was actually paid.
 *
 *  Teamlio's own status says what somebody meant to do; the portal says what
 *  happened. This walks e-Pay Tax's payment history, keeps the SALARY
 *  challans and hands them to POST /tds/challan/portal-sync, which matches
 *  them to challans here.
 *
 *  Fills in nothing and submits nothing, so it runs in a window that stays
 *  out of the way -- it only comes forward if the portal needs the person
 *  (a password it will not take, a blank Secure Access Message). */
export async function startReadPortalChallans(params: { username: string; password: string;
  authToken?: string; apiBaseUrl?: string; taxYear: string; quarter: number }) {
  const response = await sendToExtension(
    'itHistoryStart',
    { username: params.username, password: params.password,
      authToken: params.authToken, apiBaseUrl: params.apiBaseUrl,
      // The period the rows get filed under. The portal does not say which
      // quarter a payment belongs to -- Teamlio does.
      taxYear: params.taxYear, quarter: params.quarter },
    IT_TIMEOUT_MS,
  );
  if (!response?.ok) throw new Error(response?.error || 'extension_refused');
  return response;
}

export async function startFileTds({ username, password, authToken, apiBaseUrl, taxYear, section, amounts, challanId }: ItFileParams) {
  const response = await sendToExtension('itFileStart', { username, password, authToken, apiBaseUrl, taxYear, section, amounts, challanId }, IT_TIMEOUT_MS);
  if (!response?.ok) {
    throw new Error(response?.error || 'it_file_failed');
  }
  return response;
}

export interface ItUploadParams {
  username: string;
  password: string;
  authToken?: string;
  apiBaseUrl?: string;
  // "24Q" for salary. The extension matches it against the portal's form tiles.
  form: string;
  fileName: string;
  // The .fvu's bytes, base64. It comes from the department's own RPU/FVU, which
  // runs on the user's machine — hrpayroll only produces the data that goes in.
  dataBase64: string;
}

// Opens the portal, logs in, then walks e-File → Income Tax Forms → File
// Income Tax Forms → Form 24Q and puts the .fvu in the file input, so the user
// arrives with the file already attached.
//
// Stops before submitting, on purpose: submitting is what FILES the return, and
// a wrong statement filed is far worse than one not filed yet.
export async function startUpload24Q({
  username, password, authToken, apiBaseUrl, form, fileName, dataBase64,
}: ItUploadParams) {
  const response = await sendToExtension(
    'itUploadStart',
    { username, password, authToken, apiBaseUrl, form, fileName, dataBase64 },
    IT_TIMEOUT_MS,
  );
  if (!response?.ok) {
    throw new Error(response?.error || 'it_upload_failed');
  }
  return response;
}

// The manifest version of whatever build the browser actually has loaded.
// Worth asking when a message the code clearly handles comes back unhandled:
// it separates "the extension is broken" from "the browser is running a
// different copy than the folder being edited".
export async function getItExtensionVersion(): Promise<string | null> {
  try {
    const response = await sendToExtension('ping', {}, PING_TIMEOUT_MS);
    return response?.ok ? response.version || null : null;
  } catch {
    return null;
  }
}

export interface ItDebugLine {
  at: string;
  line: string;
}

// What it-extract.js logged while driving the portal. It logs to the portal
// tab's console, which is a tab the extension opened and nobody is watching —
// this is the copy the background keeps, cleared at the start of each run.
export async function getItDebugLog(): Promise<ItDebugLine[]> {
  const response = await sendToExtension('getDebugLog', {}, IT_TIMEOUT_MS);
  if (!response?.ok) {
    throw new Error(response?.error || 'debug_log_failed');
  }
  return response.lines || [];
}

function onExtensionMessage(type: string, callback: (data: any) => void) {
  const handler = (event: MessageEvent) => {
    if (event.source !== window) return;
    if ((event.data as any)?.source !== RESPONSE_SOURCE) return;
    if ((event.data as any)?.type !== type) return;
    callback(event.data);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

// Step status while the extension drives the portal: callback gets { step, message }.
export function onItStatus(callback: (step: string, message: string) => void) {
  return onExtensionMessage('itStatus', (data) => callback(data.step, data.message));
}

// Reached a point where the user needs to take over (review and pay): callback gets { message }.
export function onItReady(callback: (message: string) => void) {
  return onExtensionMessage('itReady', (data) => callback(data.message));
}

// Flow finished (amounts filled, or as far as it got): callback gets { url }.
export function onItDone(callback: (url: string) => void) {
  return onExtensionMessage('itDone', (data) => callback(data.url));
}

// The portal's challan history has been read and stored: callback gets the
// server's own summary, so the Challan Data table can re-read itself
// without polling for a run it cannot see.
export function onItHistoryDone(callback: (result: { saved?: number; recorded?: number; not_salary?: number }) => void) {
  return onExtensionMessage('itHistoryDone', (data) => callback(data.result || {}));
}

// Flow hit a fatal error: callback gets { error }.
export function onItError(callback: (error: string) => void) {
  return onExtensionMessage('itError', (data) => callback(data.error));
}
